import React, { useMemo, useRef, useEffect } from 'react';
import { Box, Typography, Alert } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Import and register the date adapter - MUST be imported before Chart.js usage
import 'chartjs-adapter-date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface RiskChartProps {
  data: any[];
}

const RiskChart: React.FC<RiskChartProps> = ({ data }) => {
  const chartRef = useRef<ChartJS<'line'> | null>(null);

  // Clean up chart on unmount to prevent canvas reuse errors
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  // Generate a unique key for the chart to force remount on data changes
  const chartKey = useMemo(() => `risk-chart-${Date.now()}`, [data]);
  
  // Process data into chart format
  const chartConfig = useMemo(() => {
    console.log('RiskChart received data:', data);
    
    if (!data || data.length === 0) {
      return null;
    }

    try {
      console.log('Processing chart data...');
      
      // Group data by risk indicator name
      const groupedData = data.reduce((acc, item) => {
        const key = item.RIName || 'Unknown Risk Indicator';
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      }, {} as Record<string, any[]>);

      console.log('Grouped data:', groupedData);

      // Create datasets for each risk indicator
      const datasets = Object.entries(groupedData).map(([riName, items], index) => {
        const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'];
        const color = colors[index % colors.length];
        
        // Sort items by date and filter out invalid data
        const sortedItems = (items as any[])
          .filter(item => {
            const hasDate = item.MonitorDate;
            const hasValue = item.WeightedValue !== null && item.WeightedValue !== undefined;
            console.log(`Item ${riName}:`, { hasDate, hasValue, item });
            return hasDate && hasValue;
          })
          .sort((a, b) => new Date(a.MonitorDate).getTime() - new Date(b.MonitorDate).getTime());
        
        console.log(`Sorted items for ${riName}:`, sortedItems);
        
        return {
          label: riName,
          data: sortedItems.map((item: any) => ({
            x: new Date(item.MonitorDate).getTime(), // Use timestamp instead of ISO string
            y: parseFloat(item.WeightedValue) || 0
          })),
          borderColor: color,
          backgroundColor: color + '20',
          fill: false,
          tension: 0.1,
          pointRadius: 4,
          pointHoverRadius: 6
        };
      }).filter(dataset => dataset.data.length > 0);

      console.log('Final datasets:', datasets);

      if (datasets.length === 0) {
        return null;
      }

      const chartData = {
        datasets
      };

      const options: ChartOptions<'line'> = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top' as const,
          },
          title: {
            display: true,
            text: 'Portfolio-Weighted Risk Indicators Over Time'
          },
          tooltip: {
            mode: 'index' as const,
            intersect: false,
            callbacks: {
              label: function(context: any) {
                return `${context.dataset.label}: ${context.parsed.y.toFixed(4)}`;
              }
            }
          }
        },
        scales: {
          x: {
            type: 'time' as const,
            time: {
              unit: 'day' as const,
              displayFormats: {
                day: 'MMM dd, yyyy'
              }
            },
            title: {
              display: true,
              text: 'Date'
            }
          },
          y: {
            title: {
              display: true,
              text: 'Risk Value'
            },
            beginAtZero: false
          }
        },
        interaction: {
          mode: 'index' as const,
          intersect: false,
        },
      };

      console.log('Chart configuration ready');
      return { data: chartData, options };
    } catch (error) {
      console.error('Error processing chart data:', error);
      return { error: error instanceof Error ? error.message : 'Unknown error occurred' };
    }
  }, [data]);

  // Handle no data
  if (!chartConfig) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No Chart Data Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click the Refresh button to load portfolio-weighted risk data for charting.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Handle processing error
  if ('error' in chartConfig) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box>
          <Alert severity="error" sx={{ mb: 2 }}>
            Chart Processing Error
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {chartConfig.error}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Please try refreshing the data or contact support.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Render chart
  return (
    <Box sx={{ height: 400, width: '100%', position: 'relative', minHeight: 400 }}>
      <Line 
        key={chartKey}
        ref={chartRef}
        data={chartConfig.data} 
        options={chartConfig.options}
      />
    </Box>
  );
};

export default RiskChart;
